/**
 * Some app data lives in the Relay store, some lives in the TanStack Query (TSQ)
 * cache, so a Relay mutation can leave TSQ-backed data stale. This module lets a
 * Relay mutation emit lifecycle events (optimistic/completed/error) on a shared
 * `EventTarget` so other data layers can subscribe and keep themselves in sync.
 *
 * This module only provides emission. Subscribing and updating/invalidating TSQ
 * query state is intentionally left to consumers of this event target.
 */

/** A singleton bus that all mutation lifecycle events are dispatched on. */
export const mutationLifecycleEventTarget: EventTarget = new EventTarget()

export type MutationLifecyclePhase = 'optimistic' | 'completed' | 'error'

export type MutationLifecycleEventDetail<TData> =
  | {phase: 'optimistic'; data: TData}
  | {phase: 'completed'; data: TData}
  | {phase: 'error'; error: Error}

/**
 * Base class for a mutation's lifecycle event. Each mutation should define its
 * own subclass (or a `type` alias) with a unique event `type` string and its own
 * response data type, then dispatch instances via `dispatchMutationLifecycleEvent`.
 *
 * Extends `Event` rather than `CustomEvent`: this module is imported by
 * SSR-rendered code (e.g. issue-viewer components import the mutation module
 * that defines these events), and unlike `Event`/`EventTarget`, `CustomEvent`
 * isn't guaranteed to exist as a global in every SSR/Node runtime. Evaluating
 * `class ... extends CustomEvent` would throw a `ReferenceError` there and break
 * server rendering, so `detail` is attached manually instead of relying on
 * `CustomEventInit`.
 */
export class MutationLifecycleEvent<TType extends string, TData> extends Event {
  readonly detail: MutationLifecycleEventDetail<TData>

  constructor(type: TType, detail: MutationLifecycleEventDetail<TData>) {
    super(type)
    this.detail = detail
  }
}

/** Dispatches a mutation lifecycle event on the shared event target. */
export function dispatchMutationLifecycleEvent<TType extends string, TData>(
  event: MutationLifecycleEvent<TType, TData>,
): void {
  mutationLifecycleEventTarget.dispatchEvent(event)
}
